import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Drawer,
  Typography,
  TextInput,
  Button,
  IconButton,
  Switch,
  Loader,
  color,
  Card,
  TextArea,
  Overlay,
  SelectDropdown
} from '@kistech/kis-ui';
import { Box as MuiBox, Typography as MuiTypography, Grid } from '@mui/material';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { actions as kioskActions, selectors as kioskSelectors, Venue } from 'store/kiosks';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { actions as CIActions, selectors as CISelectors, ClientIntegration } from 'store/clientIntegrations';
import { actions as venuesActions, selectors as venuesSelectors } from 'store/venues';
import { actions as ticketTemplatesActions, selectors as ticketTemplatesSelectors } from 'store/ticketTemplates';
import { actions as clientsActions, selectors as clientsSelectors } from 'store/clients';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'store/hardware';
import { actions as areaActions, selectors as areaSelectors, Area } from 'store/areas';
import { useStyles } from '../../styled';
import { isValidData, getDataTypedValues, TemplateTypeList, HardwareTypeList, formatConfigTitle } from '../../utils';
import { ConfigEditDrawerProps, PropertyType, TicketTemplateType, HardwareType } from '../../types';
import get from 'lodash.get';
import { getSortedList } from 'utils/stringFormat';

export const ConfigEditDrawer = (props: ConfigEditDrawerProps) => {
  const {
    isOpen,
    onClose,
    configData,
    selectedColumn,
    selectedColumnValue,
    selectedProperty: selProperty,
    selectedSubProperty,
    selectedPropertyValue,
    selectedDataType: selDataType,
    configType,
    addConfigDropdown
  } = props;

  const [
    updateClient,
    resetClientError,
    updateVenue,
    resetVenueError,
    updateClientIntegration,
    resetCIError,
    updateKiosk,
    resetKioskError,
    updateConfiguration,
    resetConfigError,
    updateHardware,
    resetHardwareError,
    updateIntegration,
    resetIntegrationError,
    updateTicketTemplate,
    resetTicketTemplateError,
    updateArea,
    resetAreaError
  ] = useActions([
    clientsActions.updateClient,
    clientsActions.resetError,
    venuesActions.updateVenue,
    venuesActions.resetError,
    CIActions.updateClientIntegration,
    CIActions.resetError,
    kioskActions.updateKiosk,
    kioskActions.resetAction,
    configActions.updateConfiguration,
    configActions.resetError,
    hardwareActions.updateHardware,
    hardwareActions.resetError,
    integrationsActions.updateIntegration,
    integrationsActions.resetError,
    ticketTemplatesActions.updateTicketTemplate,
    ticketTemplatesActions.resetError,
    areaActions.updateArea,
    areaActions.resetError
  ]);
  const clientsError = useSelector(clientsSelectors.selectError);
  const clientsPending = useSelector(clientsSelectors.selectPending);
  const { error: venuesError, pending: venuesPending } = useSelector(venuesSelectors.selectAction);
  const { error: integrationError, pending: integrationPending } = useSelector(integrationsSelectors.selectAction);
  const { error: ticketTemplatesError, pending: ticketTemplatesPending } = useSelector(
    ticketTemplatesSelectors.selectAction
  );
  const { error: areaError, pending: areaPending } = useSelector(areaSelectors.selectAction);
  const ciError = useSelector(CISelectors.selectError);
  const ciPending = useSelector(CISelectors.selectPending);
  const { error: kioskError, pending: kioskPending } = useSelector(kioskSelectors.selectAction);
  const { error: configError, pending: configPending } = useSelector(configSelectors.selectAction);
  const { error: hardwareError, pending: hardwarePending } = useSelector(hardwareSelectors.selectAction);
  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const venues = useSelector(venuesSelectors.selectVenues);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const clientIntegrations = useSelector(CISelectors.selectClientIntegrations);
  const areas = useSelector(areaSelectors.selectAreas);

  const columnKeys = addConfigDropdown
    .reduce((acc: string[], cur) => {
      acc.push(cur.key);
      return acc;
    }, [])
    .sort();

  const [propertyValue, setPropertyValue] = useState<PropertyType>('');
  const [error, setError] = useState<string>('');
  const [apiLoading, setAPILoading] = useState(false);
  const [uiLoading, setUILoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(selProperty);
  const [selectedDataType, setSelectedDataType] = useState(selDataType);
  const [selectedConfigColumn, setSelectedConfigColumn] = useState(columnKeys[0]);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration>(propertyValue as Integration);
  const [selectedType, setSelectedType] = useState<TicketTemplateType>(propertyValue as TicketTemplateType);
  const [selectedHWType, setSelectedHWType] = useState<HardwareType>(propertyValue as HardwareType);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(propertyValue as Venue);
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration>(propertyValue as Configuration);
  const [selectedCI, setSelectedCI] = useState<ClientIntegration>(propertyValue as ClientIntegration);
  const [selectedArea, setSelectedArea] = useState<Area>(propertyValue as Area);

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (propertyValue && selectedDataType === 'integration') {
      setSelectedIntegration(JSON.parse(propertyValue as string) as Integration);
    } else if (propertyValue && selectedDataType === 'configuration') {
      setSelectedConfiguration(JSON.parse(propertyValue as string) as Configuration);
    } else if (propertyValue && selectedDataType === 'clientIntegration') {
      setSelectedCI(JSON.parse(propertyValue as string) as ClientIntegration);
    } else if (propertyValue && selectedDataType === 'area') {
      setSelectedArea(JSON.parse(propertyValue as string) as Area);
    } else if (propertyValue && selectedDataType === 'venue') {
      setSelectedVenue(JSON.parse(propertyValue as string) as Venue);
    } else if (propertyValue && selectedDataType === 'templateType') {
      setSelectedType(JSON.parse(propertyValue as string) as TicketTemplateType);
    } else if (propertyValue && selectedDataType === 'hardwareType') {
      setSelectedHWType(JSON.parse(propertyValue as string) as HardwareType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyValue]);

  useEffect(() => {
    if (selectedPropertyValue && typeof selectedPropertyValue === 'object') {
      setPropertyValue(JSON.stringify(selectedPropertyValue));
    } else {
      setPropertyValue(selectedPropertyValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyValue]);

  useEffect(() => {
    if (selectedConfigColumn) {
      setSelectedProperty(selectedConfigColumn);
      setSelectedDataType(getSelectedType(selectedConfigColumn));
      if (configType === 'configuration' && selectedColumn === 'cityPass' && selectedConfigColumn === 'ticketing') {
        const cpTicketing = get(configData as Configuration, 'cityPass.ticketing');
        if (!cpTicketing || (cpTicketing && !Object.keys(cpTicketing).length)) {
          const ticketingCopy = get(configData as Configuration, 'ticketing');
          const { membershipLookup, membershipSalesSettingsList, showPriceRange, promoCode, ...cpTicketingCopy } =
            ticketingCopy;
          setPropertyValue(JSON.stringify(cpTicketingCopy));
        }
      } else {
        setPropertyValue('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfigColumn]);

  useEffect(() => {
    if (
      clientsError ||
      venuesError ||
      ciError ||
      kioskError ||
      configError ||
      ticketTemplatesError ||
      areaError ||
      integrationError ||
      hardwareError
    ) {
      setError(
        (clientsError ||
          venuesError ||
          ciError ||
          kioskError ||
          configError ||
          ticketTemplatesError ||
          areaError ||
          integrationError ||
          hardwareError) as string
      );
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientsError,
    venuesError,
    ciError,
    kioskError,
    configError,
    ticketTemplatesError,
    areaError,
    integrationError,
    hardwareError
  ]);

  useEffect(() => {
    setAPILoading(
      (clientsPending ||
        venuesPending ||
        ciPending ||
        kioskPending ||
        configPending ||
        ticketTemplatesPending ||
        areaPending ||
        integrationPending ||
        hardwarePending) as boolean
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientsPending,
    venuesPending,
    ciPending,
    kioskPending,
    configPending,
    ticketTemplatesPending,
    areaPending,
    integrationPending,
    hardwarePending
  ]);

  useEffect(() => {
    if (uiLoading && !apiLoading && !error) {
      handleOverlayClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, apiLoading]);

  const getPayload = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      id: configData && configData.id
    };
    switch (selectedDataType) {
      case 'integration':
        payload = { ...payload, integrationId: selectedIntegration.id };
        break;
      case 'clientIntegration':
        payload = { ...payload, clientIntegrationId: selectedCI.id };
        break;
      case 'venue':
        payload = { ...payload, venueId: selectedVenue.id };
        break;
      case 'area':
        payload = { ...payload, areaId: selectedArea.id };
        break;
      case 'templateType':
        payload = { ...payload, type: selectedType.code };
        break;
      case 'hardwareType':
        payload = { ...payload, type: selectedHWType.code };
        break;
      case 'configuration':
        payload = { ...payload, configurationId: selectedConfiguration.id };
        break;
      default:
        if (selectedSubProperty && typeof selectedColumnValue === 'object') {
          payload = {
            ...payload,
            [selectedColumn]: {
              ...selectedColumnValue,
              [selectedSubProperty]: getDataTypedValues(
                selectedDataType,
                propertyValue,
                selectedProperty,
                selectedSubProperty,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (selectedColumnValue as Record<string, any>)[selectedSubProperty]
              )
            }
          };
        } else {
          payload = {
            ...payload,
            [selectedColumn]: getDataTypedValues(
              selectedDataType,
              propertyValue,
              selectedProperty,
              selectedColumn,
              selectedColumnValue
            )
          };
        }
        break;
    }

    return payload;
  };

  const onConfirm = () => {
    resetError();
    if (isValidData(propertyValue, selectedDataType)) {
      setUILoading(true);
      const payload = getPayload();
      if (configType === 'configuration') {
        updateConfiguration(payload);
      } else if (configType === 'kiosk') {
        updateKiosk(payload);
      } else if (configType === 'clientIntegration') {
        updateClientIntegration(payload);
      } else if (configType === 'venue') {
        updateVenue(payload);
      } else if (configType === 'area') {
        updateArea(payload);
      } else if (configType === 'client') {
        updateClient(payload);
      } else if (configType === 'hardware') {
        updateHardware(payload);
      } else if (configType === 'integration') {
        updateIntegration(payload);
      } else if (configType === 'ticketTemplate') {
        updateTicketTemplate(payload);
      }
    } else {
      setError('Invalid data for the property');
    }
  };

  const handleOverlayClose = () => {
    resetError();
    onClose();
  };

  const resetError = () => {
    if (error) {
      setError('');
      if (clientsError) {
        resetClientError();
      } else if (venuesError) {
        resetVenueError();
      } else if (areaError) {
        resetAreaError();
      } else if (ciError) {
        resetCIError();
      } else if (kioskError) {
        resetKioskError();
      } else if (configError) {
        resetConfigError();
      } else if (hardwareError) {
        resetHardwareError();
      } else if (integrationError) {
        resetIntegrationError();
      } else if (ticketTemplatesError) {
        resetTicketTemplateError();
      }
    }
  };

  const getSelectedType = (key: string): string => {
    const selected = addConfigDropdown.find((column) => column.key === key);
    return selected ? selected.type : 'object';
  };

  return (
    <>
      {apiLoading && (
        <Overlay>
          <Loader color={color.LIGHT_GRAY} />
        </Overlay>
      )}
      <Drawer isOpen={isOpen} onClose={handleOverlayClose} hasBorderRadius>
        <Grid container className={classes.configEditOverlay}>
          <Card fullHeight>
            <Grid className={classes.configOverlayHeaderBlock}>
              <Grid className={classes.configOverlayHeading}>
                <Flex>
                  <MuiTypography className={classes.configEditHeading} style={{ fontWeight: 600 }}>
                    {configData && configData.name}
                  </MuiTypography>
                </Flex>
              </Grid>
              <Grid className={classes.configOverlayCloseIcon}>
                <IconButton
                  name="exit_circle"
                  appearance="transparent"
                  size={40}
                  onClick={handleOverlayClose}
                  data-testid="kiosk-restock-x"
                />
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography weight={600} size={16}>
                {formatConfigTitle(selectedColumn, selectedDataType)}
                {selectedSubProperty ? ` / ${formatConfigTitle(selectedSubProperty)}` : ''}
              </Typography>
            </Box>
            <Card.Divider color={color.BORDER} />
            <MuiBox className={classes.configContentBox}>
              <Box margin="6 0 5" flexOverride="none">
                {addConfigDropdown && addConfigDropdown.length ? (
                  <SelectDropdown
                    items={columnKeys ? columnKeys : ['']}
                    itemToString={(column: string) => column}
                    selectedItem={selectedConfigColumn}
                    onChange={(column: string) => setSelectedConfigColumn(column)}
                    placeholder="Select a Property"
                    data-testid="media-selector"
                  />
                ) : (
                  <MuiTypography className={classes.configProperty}>
                    {formatConfigTitle(selectedProperty, selectedDataType)}
                  </MuiTypography>
                )}
              </Box>
              <MuiBox className={classes.configTextBox}>
                {(selectedDataType === 'object' || selectedDataType === 'array' || selectedDataType === 'string') && (
                  <TextArea
                    size={'medium'}
                    style={{ minWidth: '400px', height: '475px' }}
                    value={
                      propertyValue && typeof propertyValue === 'object'
                        ? JSON.stringify(propertyValue)
                        : String(propertyValue)
                    }
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.value);
                    }}
                    autoFocus
                  />
                )}
                {selectedDataType === 'number' && (
                  <TextInput
                    type="text"
                    size="medium"
                    align="center"
                    fullWidth
                    autoFocus
                    value={propertyValue || propertyValue === 0 ? propertyValue.toString() : ''}
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.value);
                    }}
                  />
                )}
                {selectedDataType === 'boolean' && (
                  <Switch
                    checked={propertyValue === true || propertyValue === 'true'}
                    checkedLabel="TRUE"
                    uncheckedLabel="FALSE"
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.checked.toString());
                    }}
                  />
                )}
                {selectedDataType === 'integration' && (
                  <SelectDropdown
                    placeholder={`Select an Integration`}
                    items={getSortedList(integrations, 'name')}
                    selectedItem={selectedIntegration}
                    onChange={(item: Integration) => setSelectedIntegration(item)}
                    itemToString={(item: Integration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'templateType' && (
                  <SelectDropdown
                    placeholder={`Select a Type`}
                    items={getSortedList(TemplateTypeList as TicketTemplateType[], 'name')}
                    selectedItem={selectedType as TicketTemplateType}
                    onChange={(item: TicketTemplateType) => setSelectedType(item)}
                    itemToString={(item) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'hardwareType' && (
                  <SelectDropdown
                    placeholder={`Select a Type`}
                    items={getSortedList(HardwareTypeList as HardwareType[], 'name')}
                    selectedItem={selectedHWType as HardwareType}
                    onChange={(item: HardwareType) => setSelectedHWType(item)}
                    itemToString={(item) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'venue' && (
                  <SelectDropdown
                    placeholder={`Select a Venue`}
                    items={getSortedList(venues, 'name')}
                    selectedItem={selectedVenue}
                    onChange={(item: Venue) => setSelectedVenue(item)}
                    itemToString={(item: Venue) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'configuration' && (
                  <SelectDropdown
                    placeholder={`Select a Configuration`}
                    items={getSortedList(configurations, 'name')}
                    selectedItem={selectedConfiguration}
                    onChange={(item: Configuration) => setSelectedConfiguration(item)}
                    itemToString={(item: Configuration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'clientIntegration' && (
                  <SelectDropdown
                    placeholder={`Select a Client Integration`}
                    items={getSortedList(clientIntegrations, 'name')}
                    selectedItem={selectedCI}
                    onChange={(item: ClientIntegration) => setSelectedCI(item)}
                    itemToString={(item: ClientIntegration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'area' && (
                  <SelectDropdown
                    placeholder={`Select an Area`}
                    items={getSortedList(areas, 'name')}
                    selectedItem={selectedArea}
                    onChange={(item: Area) => setSelectedArea(item)}
                    itemToString={(item: Area) => (item ? item.name : '')}
                  />
                )}
              </MuiBox>
              <Box margin="1 0 0">
                <Typography color={color.RED} size={14}>
                  {error}
                </Typography>
              </Box>
              {/* button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button size="small" appearance="primary" onClick={onConfirm} className={classes.saveButton}>
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
